import axios, { AxiosResponse } from 'axios';

import { getConfig } from '../config';
import { PaymentMethod, StripeAccount } from '../database/payment';
import { SetUpFuturePaymentsRequest } from './theSourceApi';

const BASE_URL = getConfig().api.baseUrl;
export interface PaymentApiContextType {
    /**
     * Creates a checkout session.
     * @returns A subscription checkout session URL.
     */
    setUpFuturePayment: (req: SetUpFuturePaymentsRequest) => Promise<AxiosResponse<StripeUrlResponse>>;

    /**
     * Creates a Stripe account for the current user.
     * @returns A Stripe account link URL.
     */
    createPaymentAccount: () => Promise<AxiosResponse<StripeUrlResponse>>;

    getPaymentAccount: () => Promise<AxiosResponse<StripeAccount>>;

    paymentAccountLogin: () => Promise<AxiosResponse<string>>;

    getUserPaymentInfo: () => Promise<AxiosResponse<PaymentMethod>>;

    /**
     * Creates a subscription checkout session.
     * @param request The SubscriptionCheckoutRequest.
     * @returns A subscription checkout session URL.
     */
    subscriptionCheckout: (
        request: SubscriptionCheckoutRequest,
    ) => Promise<AxiosResponse<StripeUrlResponse>>;

    /**
     * Creates a subscription manage session.
     * @returns A subscription manage session URL.
     */
    subscriptionManage: () => Promise<AxiosResponse<StripeUrlResponse>>;
}

/** A common response to Stripe requests. Contains a URL to Stripe workflows. */
export interface StripeUrlResponse {
    /** The Stripe URL. */
    url: string;
}

/** A request to create a subscription checkout session. */
export interface SubscriptionCheckoutRequest {
    /** Where to redirect the user upon success. */
    successPath?: string;

    /** Where to redirect the user if they cancel. */
    cancelPath?: string;
}

export function setUpFuturePayment(idToken: string, req: SetUpFuturePaymentsRequest) {
    return axios.post<StripeUrlResponse>(
        `${BASE_URL}/payment/setup`,
        req,
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

/**
 * Creates a Stripe account for the current user.
 * @param idToken The id token of the current signed-in user.
 * @returns A Stripe account link URL.
 */
export function createPaymentAccount(idToken: string) {
    return axios.put<StripeUrlResponse>(
        `${BASE_URL}/payment/account`,
        {},
        {
            headers: {
                Authorization: 'Bearer ' + idToken,
            },
        },
    );
}

/**
 * Gets the Stripe account for the current user.
 * @param idToken The id token of the current signed-in user.
 * @returns A Stripe account.
 */
export function getPaymentAccount(idToken: string) {
    return axios.get<StripeAccount>(`${BASE_URL}/payment/account`, {
        headers: {
            Authorization: 'Bearer ' + idToken,
        },
    });
}

export function paymentAccountLogin(idToken: string) {
    return axios.get<string>(`${BASE_URL}/payment/login`, {
        headers: {
            Authorization: 'Bearer ' + idToken,
        },
    });
}

export function getUserPaymentInfo(idToken: string) {
    return axios.get<PaymentMethod>(`${BASE_URL}/payment/method`, {
        headers: {
            Authorization: 'Bearer ' + idToken,
        }
    })
}

/**
 * Creates a subscription checkout session.
 * @param idToken The id token of the current signed-in user.
 * @param request The SubscriptionCheckoutRequest.
 * @returns A subscription checkout session URL.
 */
export function subscriptionCheckout(idToken: string, request: SubscriptionCheckoutRequest) {
    return axios.post<StripeUrlResponse>(`${BASE_URL}/subscription/checkout`, request, {
        headers: {
            Authorization: 'Bearer ' + idToken,
        },
    });
}

/**
 * Creates a subscription manage session.
 * @param idToken The id token of the current signed-in user.
 * @returns A subscription manage session URL.
 */
export function subscriptionManage(idToken: string) {
    return axios.post<StripeUrlResponse>(
        `${BASE_URL}/subscription/manage`,
        {},
        { headers: { Authorization: 'Bearer ' + idToken } },
    );
}